import { useState } from 'react';
import { Sparkles, Copy, Check, Download, Loader2, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const POST_TYPES = [
  { id: 'promotional', label: 'Promotional', desc: 'Highlight rooms & pricing' },
  { id: 'fun-fact', label: 'Fun Fact', desc: 'Escape room tips & trivia' },
  { id: 'team-building', label: 'Team Building', desc: 'Target military/corporate' },
  { id: 'weekend-special', label: 'Weekend Special', desc: 'Weekend booking push' },
  { id: 'challenge', label: 'Challenge', desc: 'Dare followers to escape' },
  { id: 'holiday', label: 'Holiday', desc: 'Seasonal themed post' },
];

export default function AutoPostGenerator() {
  const [postType, setPostType] = useState('promotional');
  const [topic, setTopic] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/post/auto-generate`, { post_type: postType, topic }, { withCredentials: true });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate');
    }
    setGenerating(false);
  };

  const handleCopy = () => {
    if (!result?.post_text) return;
    navigator.clipboard.writeText(result.post_text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadImage = () => {
    if (!result?.image_base64) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${result.image_base64}`;
    link.download = `okiescape-post-${postType}.png`;
    link.click();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <h2 className="font-heading text-2xl tracking-wide uppercase text-white mb-2">
        Auto Post <span className="text-brand">Generator</span>
      </h2>
      <p className="font-sans text-sm text-neutral-500 mb-6">One-click generate a complete Facebook post with matching image. Copy text, download image, post to your page.</p>

      {/* Post type selector */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
        {POST_TYPES.map(pt => (
          <button key={pt.id} onClick={() => setPostType(pt.id)} data-testid={`type-${pt.id}`} className={`p-3 rounded-xl border text-left transition-all ${postType === pt.id ? 'border-brand bg-brand/10' : 'border-neutral-800 hover:border-neutral-600'}`}>
            <p className="font-heading text-xs tracking-wide uppercase text-white">{pt.label}</p>
            <p className="font-sans text-[10px] text-neutral-500 mt-0.5">{pt.desc}</p>
          </button>
        ))}
      </div>

      {/* Optional topic */}
      <div className="flex gap-3 mb-6">
        <input type="text" value={topic} onChange={e => setTopic(e.target.value)} placeholder="Optional: specific topic or angle..." className="flex-1 bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-3 text-white font-sans text-sm focus:border-brand focus:outline-none" />
        <button onClick={handleGenerate} disabled={generating} data-testid="auto-generate-btn" className="flex items-center gap-2 font-heading text-xs tracking-widest uppercase bg-brand text-white px-6 py-3 rounded-xl hover:bg-brand-hover transition-colors disabled:opacity-50">
          {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {generating ? 'Generating...' : 'Generate'}
        </button>
      </div>

      {error && <p className="text-brand font-sans text-sm bg-brand/10 border border-brand/30 rounded-xl p-3 mb-4">{error}</p>}

      {generating && (
        <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-12 text-center">
          <Loader2 size={48} className="text-brand animate-spin mx-auto mb-4" />
          <p className="font-heading text-lg uppercase text-neutral-400">Creating post + image...</p>
          <p className="font-sans text-xs text-neutral-600 mt-2">This generates both the caption and a matching image (up to 90 seconds)</p>
        </div>
      )}

      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Post text */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-base tracking-wide uppercase text-neutral-400">Post Caption</h3>
              <button onClick={handleCopy} data-testid="auto-copy-btn" className="flex items-center gap-1 font-sans text-xs text-neutral-400 hover:text-brand transition-colors">
                {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy Text</>}
              </button>
            </div>
            <div className="bg-black rounded-xl p-4 border border-neutral-800">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-brand flex items-center justify-center font-heading text-white text-sm">OE</div>
                <div>
                  <p className="font-sans text-sm font-bold text-white">OKI-ESCAPE</p>
                  <p className="font-sans text-xs text-neutral-500">Just now</p>
                </div>
              </div>
              <div className="font-sans text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed">{result.post_text}</div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={handleCopy} className="flex-1 flex items-center justify-center gap-2 font-heading text-xs tracking-widest uppercase bg-neutral-800 text-white py-3 rounded-xl hover:bg-neutral-700 transition-colors">
                <Copy size={14} /> Copy Caption
              </button>
              <button onClick={handleGenerate} disabled={generating} className="flex items-center justify-center gap-2 font-heading text-xs tracking-widest uppercase bg-neutral-800 text-white px-4 py-3 rounded-xl hover:bg-neutral-700 transition-colors">
                <RefreshCw size={14} />
              </button>
            </div>
          </div>

          {/* Generated image */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
            <h3 className="font-heading text-base tracking-wide uppercase text-neutral-400 mb-4">Post Image</h3>
            {result.image_base64 ? (
              <div className="space-y-4">
                <img src={`data:image/png;base64,${result.image_base64}`} alt="Generated post image" className="w-full rounded-xl shadow-lg" />
                <button onClick={handleDownloadImage} data-testid="auto-download-btn" className="w-full flex items-center justify-center gap-2 font-heading text-xs tracking-widest uppercase bg-neutral-800 text-white py-3 rounded-xl hover:bg-neutral-700 transition-colors">
                  <Download size={14} /> Download Image
                </button>
              </div>
            ) : (
              <div className="text-center py-12 text-neutral-600">
                <p className="font-sans text-sm">No image generated</p>
              </div>
            )}
          </div>
        </div>
      )}

      {!generating && !result && (
        <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-12 text-center">
          <Sparkles size={48} className="text-neutral-700 mx-auto mb-4" />
          <p className="font-heading text-lg uppercase text-neutral-500">Ready to Generate</p>
          <p className="font-sans text-xs text-neutral-600 mt-2">Pick a post type, optionally add a topic, and hit Generate</p>
          <p className="font-sans text-xs text-neutral-600 mt-1">You'll get a caption + matching image to post on your Facebook page</p>
        </div>
      )}
    </div>
  );
}
